import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Card } from 'design-system';
import { Typography } from 'design-system/compat';
import { logClientError } from '../service/logging';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, showDetails: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    logClientError(error, info, { scope: 'global' });
  }

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    const { hasError, showDetails, error, info } = this.state;
    if (!hasError) return this.props.children;

    return (
      <div style={{ padding: 16, maxWidth: 720, margin: '40px auto' }} role="alert">
        <Card>
          <Typography.Title level={3}>
            <FormattedMessage id="error_boundary_title" defaultMessage="Something went wrong" />
          </Typography.Title>
          <Typography style={{ marginBottom: 12 }}>
            <FormattedMessage
              id="error_boundary_subtitle"
              defaultMessage="An unexpected error occurred. You can try reloading the page. If the problem persists, our team will investigate."
            />
          </Typography>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button onClick={this.handleReload}>
              <FormattedMessage id="error_boundary_reload" defaultMessage="Reload page" />
            </Button>
            <Button variant="outlined" onClick={() => this.setState({ showDetails: !showDetails })}>
              {showDetails ? (
                <FormattedMessage id="error_boundary_hide_details" defaultMessage="Hide details" />
              ) : (
                <FormattedMessage id="error_boundary_show_details" defaultMessage="Show details" />
              )}
            </Button>
          </div>
          {showDetails && (
            <pre
              style={{
                marginTop: 12,
                padding: 12,
                border: '1px solid var(--surface-3)',
                borderRadius: 8,
                background: 'var(--surface-2)',
                overflowX: 'auto',
                maxHeight: 320,
              }}
              aria-label="Error details"
            >
              {(error?.stack || String(error)) + (info?.componentStack ? `\n\n${info.componentStack}` : '')}
            </pre>
          )}
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;

